import { TextField } from 'react-md'
import type { TextFieldProps } from 'react-md'
import { parseNumericInput } from 'util/parseNumericInput'

type NumericTextFieldProps = Omit<
	TextFieldProps,
	'onChange' | 'value' | 'text' | 'inputMode' | 'type'
> & {
	value: string | number | string[] | undefined
	onChange: (value: string | number) => void
	autoComplete?: string
	min?: number
	allowDecimal?: boolean
}

/**
 * NumericTextField is a custom input component for typing and validating numeric input,
 * including support for negative numbers, decimals, and partial typing states like `-`, `0.`, or `-.`.
 *
 * Props:
 * - `value`: A `string` or `number` representing the current value of the input.
 *   - Can be a number (e.g., 12.5) or a string during intermediate typing (e.g., "0.", "-").
 *   - You must manage the state as either number or string depending on user input.
 *
 * - `onChange`: A callback that receives the new value when input changes.
 *   - Called with a string when input is incomplete (e.g., "-", "0.").
 *   - Called with a number when the input is a valid, complete number (e.g., 123.45).
 *
 * - `min`: Optional. If provided and >= 0, negative numbers and typing "-" are disallowed.
 *
 * - `allowDecimal`: Optional boolean (default: true). If false, decimal input is disallowed.
 *
 * Behavior:
 * - Only allows valid numeric characters ("0-9", optional "-", and optional ".").
 * - Prevents multiple "." or "-" characters and strips invalid characters.
 * - Preserves typing intent by allowing incomplete but valid intermediate values.
 * - Prevents conversion to number too early, avoiding undesired auto-correction like "0." -> 0
 *
 */
export function NumericTextField({
	value,
	onChange,
	autoComplete = 'never-autocomplete',
	min,
	max,
	allowDecimal = true,
	...rest
}: NumericTextFieldProps) {
	return (
		<TextField
			{...rest}
			type="text"
			value={String(value)}
			onChange={(val) =>
				parseNumericInput(val, onChange, { min, max, allowDecimal })
			}
			//@ts-ignore
			inputMode="numeric"
			//@ts-ignore
			autoComplete={autoComplete}
		/>
	)
}
